import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get } from '@app/api/typed';
import { t } from '@app/i18n';
import type { components } from '@app/api/types';
import type { ComboOption } from '@zarya/ui';

/**
 * O **dono** — de um resource (T7) e, desde 28/08, de uma identidade (T5).
 *
 * Os dois campos são a mesma coisa vista de telas diferentes: uma pessoa
 * cadastrada em Configurações › Usuários, gravada como `Nome <e-mail>`. Este
 * módulo é o único lugar que sabe montar esse texto e essas opções — antes ele
 * vivia dentro do `ResourceForm`, e a segunda tela teria copiado a regra.
 */

export type ApplicationUser = components['schemas']['User'];

/** Quem está logado, o mínimo que a opção «(você)» precisa. */
export interface OwnerPerson {
  name: string;
  email: string;
}

/**
 * O texto que os campos **Dono** gravam: `Nome <e-mail>`.
 *
 * O contrato mantém `Resource.owner` e `Identity.dono` como string livre; é a
 * tela que restringe a escolha aos usuários da aplicação. Um valor gravado antes
 * dessa regra — texto livre, ou o `owner` que veio do APIM na importação —
 * continua legível e selecionável até alguém trocá-lo.
 */
export function userOwner(usuario: OwnerPerson): string {
  return `${usuario.name} <${usuario.email}>`;
}

/**
 * Só o nome de um dono gravado, para grades e listas: `Ana Ribeiro <ana@x>` vira
 * `Ana Ribeiro`. O e-mail continua existindo no valor — quem exibe o nome curto
 * põe o texto inteiro no `title`, senão a coluna esconde qual das duas Anas é.
 *
 * Valor legado sem `<…>` volta inteiro: é o que a pessoa escreveu, e recortá-lo
 * seria inventar um formato que ele nunca teve.
 */
export function ownerName(dono: string | null | undefined): string {
  const texto = (dono ?? '').trim();
  const opens = texto.lastIndexOf('<');
  if (opens <= 0 || !texto.endsWith('>')) return texto;
  return texto.slice(0, opens).trim();
}

/** Duas pessoas são a mesma quando o e-mail é o mesmo (o contrato o faz único). */
function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface OwnerInputOptions {
  /** Usuários da aplicação (`listUsers`). Vazio quando quem olha não pode listá-los. */
  users: ApplicationUser[];
  /** Quem está logado — vira a **primeira** opção, com o sufixo «(você)». */
  eu?: OwnerPerson | null;
  /** O valor que está no formulário, para o gatilho ter rótulo. */
  atual: string;
}

/**
 * As opções do combobox de dono, nesta ordem:
 *
 *  1. **o usuário logado, rotulado «(você)»** — como Jira e GitLab. Quem preenche
 *     o campo é, na maioria das vezes, quem assume o agente ou o recurso, e
 *     procurar o próprio nome numa lista de duzentos é trabalho à toa;
 *  2. os demais usuários da aplicação;
 *  3. o **valor gravado**, quando não é nenhum deles.
 *
 * A terceira entrada existe porque o combobox rotula o gatilho pelo que está em
 * `options`: sem ela, um dono legado apareceria como campo vazio — o que diria
 * "sem dono" sobre uma linha que tem um. Ela vai no fim, e não no começo, para
 * não empurrar o «(você)» da primeira posição.
 *
 * O logado entra mesmo quando não está em `usuarios`: `listUsers` é de admin, e
 * um approver — que **pode** atribuir identidade — recebe a lista vazia. Sem
 * esta regra a tela dele não ofereceria dono nenhum, nem ele mesmo.
 */
export function ownerOptions({ users, eu, atual }: OwnerInputOptions): ComboOption[] {
  const opcoes: ComboOption[] = [];

  if (eu) {
    opcoes.push({
      value: userOwner(eu),
      label: t('common.owner.me', { name: eu.name }),
      hint: eu.email,
    });
  }
  for (const usuario of users) {
    if (eu && sameEmail(usuario.email, eu.email)) continue;
    opcoes.push({ value: userOwner(usuario), label: usuario.name, hint: usuario.email });
  }
  if (atual && !opcoes.some((opcao) => opcao.value === atual)) {
    opcoes.push({ value: atual, label: atual, hint: t('common.owner.storedValue') });
  }
  return opcoes;
}

/** Chave de cache dos usuários do combobox — uma só, compartilhada por T5 e T7. */
export const ownerKeys = { users: ['usuarios-para-dono'] as const };

/**
 * Usuários da aplicação para os campos **Dono**.
 *
 * `ativo` tem de ser a permissão de `listUsers` (admin), **não** a de editar a
 * tela: `assignIdentity` é de admin e approver, e um approver que disparasse
 * esta consulta tomaria 403 ao abrir o drawer. Sem a lista o combobox ainda
 * oferece o «(você)» e o valor gravado, que é o suficiente para salvar.
 */
export function useUsersForOwner(ativo: boolean): UseQueryResult<ApplicationUser[], ApiError> {
  return useQuery<ApplicationUser[], ApiError>({
    queryKey: ownerKeys.users,
    enabled: ativo,
    queryFn: async () => {
      const page = await get('/api/v1/users', { params: { page: 1, size: 200, sort: 'name' } });
      return (page.items ?? []) as ApplicationUser[];
    },
  });
}
